import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  Box,
  useToast,
} from '@chakra-ui/react';
import { FileUploadContextDirect } from '@duke-hq/svc-files-ui/FileUploadContextDirect';
import { FileUploadDropzone } from '@duke-hq/svc-files-ui/FileUploadDropzone';
import { FileUploadList } from '@duke-hq/svc-files-ui/FileUploadList';
import { FileUploadTrigger } from '@duke-hq/svc-files-ui/FileUploadTrigger';
import { FileUploadContext } from '@duke-hq/svc-files-ui/FileUploadContext';
import { PrePlacementDocumentType } from '@duke-hq/svc-api-db';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useContext, useRef, useState } from 'react';
import { Select } from 'chakra-react-select';
import type { Drn } from '@duke-hq/drn/type';
import { getDocumentTypeLabel } from './documentTypeLabels';
import { testIds } from './testIds';

interface UploadDocumentModalProps {
  journeyId: string;
  documentType: PrePlacementDocumentType | null;
  availableTypes: PrePlacementDocumentType[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ACCEPT = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

const MAX_SIZE_MB = 25;
const MAX_FILES = 5;

export function UploadDocumentModal({
  journeyId,
  documentType: initialDocumentType,
  availableTypes,
  isOpen,
  onClose,
  onSuccess,
}: UploadDocumentModalProps): React.ReactElement {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const uploadMutation = trpcAdmin.workPlacements.placements.uploadDocumentOnBehalf.useMutation();
  const [selectedType, setSelectedType] = useState<PrePlacementDocumentType | null>(
    initialDocumentType,
  );
  const fileDrnsRef = useRef<Drn[]>([]);

  const documentType = initialDocumentType ?? selectedType;

  const handleFileComplete = (fileDrn: Drn) => {
    fileDrnsRef.current = [...fileDrnsRef.current, fileDrn];
  };

  const handleSubmit = async () => {
    if (documentType == null || fileDrnsRef.current.length === 0) {
      return;
    }

    try {
      await uploadMutation.mutateAsync({
        journeyId,
        documentType,
        fileDrns: fileDrnsRef.current,
      });
      await utils.workPlacements.placements.getChecklist.invalidate({ journeyId });
      toast({
        status: 'success',
        title: t`Document uploaded`,
        duration: 4000,
        isClosable: true,
      });
      fileDrnsRef.current = [];
      onSuccess();
    } catch (error) {
      toast({
        status: 'error',
        title: t`Upload failed`,
        description: error instanceof Error ? error.message : t`Please try again.`,
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    if (!uploadMutation.isPending) {
      setSelectedType(initialDocumentType);
      fileDrnsRef.current = [];
      onClose();
    }
  };

  return (
    <FileUploadContextDirect
      quantity={{ min: 1, max: MAX_FILES }}
      accept={ACCEPT}
      maxSizePerFileMB={MAX_SIZE_MB}
      attachedToId={journeyId}
      attachedToKind="WORK_PLACEMENT_DOCUMENT"
      visibility="private"
      onFileComplete={(output) => {
        handleFileComplete(output.drn);
      }}
    >
      <UploadDocumentModalInner
        isOpen={isOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        documentType={documentType}
        showTypeSelector={initialDocumentType == null}
        availableTypes={availableTypes}
        selectedType={selectedType}
        onTypeChange={setSelectedType}
        isMutationPending={uploadMutation.isPending}
      />
    </FileUploadContextDirect>
  );
}

function UploadDocumentModalInner({
  isOpen,
  onClose,
  onSubmit,
  documentType,
  showTypeSelector,
  availableTypes,
  selectedType,
  onTypeChange,
  isMutationPending,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  documentType: PrePlacementDocumentType | null;
  showTypeSelector: boolean;
  availableTypes: PrePlacementDocumentType[];
  selectedType: PrePlacementDocumentType | null;
  onTypeChange: (type: PrePlacementDocumentType | null) => void;
  isMutationPending: boolean;
}): React.ReactElement {
  const fileUploadContext = useContext(FileUploadContext);
  const isUploading = fileUploadContext?.hasPendingFileUploads ?? false;
  const hasFiles =
    (fileUploadContext?.files.filter((f) => f.status === 'complete').length ?? 0) > 0;
  const isBusy = isUploading || isMutationPending;

  const guardedOnClose = () => {
    if (!isBusy) {
      onClose();
    }
  };

  const labels = getDocumentTypeLabel();
  const typeOptions = availableTypes.map((type) => ({ label: labels[type], value: type }));

  return (
    <Modal isOpen={isOpen} onClose={guardedOnClose} size="2xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent data-testid={testIds.uploadDocumentModal}>
        <ModalHeader>
          <Trans>Upload document</Trans>
        </ModalHeader>
        <ModalCloseButton isDisabled={isBusy} />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            {showTypeSelector ? (
              <FormControl isRequired>
                <FormLabel>
                  <Trans>Document type</Trans>
                </FormLabel>
                <Select<{ label: string; value: PrePlacementDocumentType }>
                  data-testid={testIds.uploadDocumentTypeSelect}
                  options={typeOptions}
                  value={typeOptions.find((o) => o.value === selectedType) ?? null}
                  onChange={(option) => onTypeChange(option?.value ?? null)}
                  placeholder={t`Select a document type`}
                />
              </FormControl>
            ) : (
              <Text fontSize="sm" color="fg.muted">
                {documentType != null ? labels[documentType] : ''}
              </Text>
            )}
            {documentType != null ? (
              <>
                <FileUploadList />
                <FileUploadDropzone />
              </>
            ) : null}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack justify="space-between" w="full">
            {documentType != null ? (
              <FileUploadTrigger>
                <Button variant="ghost">
                  <Trans>Choose file</Trans>
                </Button>
              </FileUploadTrigger>
            ) : (
              <Box />
            )}
            <Button
              colorScheme="brand"
              onClick={onSubmit}
              isLoading={isMutationPending}
              isDisabled={!hasFiles || isBusy}
            >
              <Trans>Submit</Trans>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
